import { Divider, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Box, Stack } from "@mui/system";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { GeneratorStatusGrid } from "./GeneratorStatusGrid";
import { MonthDetailsComparison } from "./MonthDetailsComparison";
import { OverThresholdDot } from "./OverThresholdDot";

type MonthDataDetailsProps = {
  month: string;
  vendorHazardousAmount: number;
  eManifestHazardousAmount: number;
  vendorAcutelyHazardousAmount: number;
  eManifestAcutelyHazardousAmount: number;
  vendorStateRegulatedAmount: number;
  eManifestStateRegulatedAmount: number;
  reportedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  reportedStateGeneratorStatus?: StateRegulatoryStatus | null;
  calculatedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedStateGeneratorStatus?: StateRegulatoryStatus | null;
  isOverReportedThreshold: boolean;
  showStateRegulatedWaste?: boolean;
  facilityState?: string | null;
  availableMonths: string[];
  onMonthChange: (rawMonth: string) => void;
} | null;

export function MonthDataDetails(props: MonthDataDetailsProps) {
  if (!props || !props.month) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="textSecondary">Select a month to view details</Typography>
      </Box>
    );
  }

  const {
    month,
    vendorHazardousAmount,
    eManifestHazardousAmount,
    vendorAcutelyHazardousAmount,
    eManifestAcutelyHazardousAmount,
    vendorStateRegulatedAmount,
    eManifestStateRegulatedAmount,
    reportedFederalGeneratorStatus,
    reportedStateGeneratorStatus,
    calculatedFederalGeneratorStatus,
    calculatedStateGeneratorStatus,
    isOverReportedThreshold,
    showStateRegulatedWaste = false,
    facilityState,
    availableMonths,
    onMonthChange,
  } = props;

  return (
    <Box sx={{ p: 3, pt: 1, backgroundColor: grey[100], borderRadius: "4px" }}>
      <Stack spacing={1}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // Single column on small screens
              sm: "1fr 1fr", // Two columns on larger screens
            },
            gap: 1,
            columnGap: 3,
            pb: 1,
          }}
        >
          <Stack
            direction="row"
            gap={3}
            alignItems={showStateRegulatedWaste ? "flex-start" : "center"}
            flexWrap="wrap" // Allow wrapping on small screens
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="subtitle1" color="textPrimary" mb={2}>
                Detailed Month View
              </Typography>
              <FormControl
                variant="standard"
                sx={{
                  width: "100%",
                }}
              >
                <InputLabel id="month-select-label">Month</InputLabel>
                <Select
                  labelId="month-select-label"
                  value={month}
                  label="Month"
                  onChange={(event) => {
                    onMonthChange(event.target.value);
                  }}
                  sx={{ fontWeight: "bold" }}
                >
                  {availableMonths.map((monthOption) => (
                    <MenuItem key={monthOption} value={monthOption}>
                      {formatMonthHeader(monthOption)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <GeneratorStatusGrid
                calculatedFederalGeneratorStatus={calculatedFederalGeneratorStatus}
                reportedFederalGeneratorStatus={reportedFederalGeneratorStatus}
                calculatedStateGeneratorStatus={calculatedStateGeneratorStatus}
                reportedStateGeneratorStatus={reportedStateGeneratorStatus}
                state={facilityState}
                showStateRegulatedWaste={showStateRegulatedWaste}
              />
            </Box>
          </Stack>

          {isOverReportedThreshold ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center", // Center the container vertically
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start", // Align contents to top within the container
                  width: "100%",
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <OverThresholdDot fontSize={20} />
                </Box>
                <Typography variant="caption" color="textSecondary" fontStyle="italic">
                  Over reported threshold. Contact Encamp about updating this generator status in
                  order to mitigate regulatory risk
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box /> // Empty box to maintain grid structure
          )}
        </Box>

        <Divider />

        <MonthDetailsComparison
          vendorHazardousAmount={vendorHazardousAmount}
          eManifestHazardousAmount={eManifestHazardousAmount}
          vendorAcutelyHazardousAmount={vendorAcutelyHazardousAmount}
          eManifestAcutelyHazardousAmount={eManifestAcutelyHazardousAmount}
          vendorStateRegulatedAmount={vendorStateRegulatedAmount}
          eManifestStateRegulatedAmount={eManifestStateRegulatedAmount}
          showStateRegulatedWaste={showStateRegulatedWaste}
        />
      </Stack>
    </Box>
  );
}

function formatMonthHeader(date: string): string {
  const month = DateTime.fromFormat(date, "yyyy-MM-dd");
  return month.toFormat("LLLL yyyy");
}
